// src/calls/service/media.js
const log = (...a) => console.log('[CALLDBG][PC]', ...a);

export async function getMedia(svc, video) {
    const audio = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
    const v = video ? { facingMode: svc._facing || 'user' } : false;
    log('getUserMedia constraints', { audio, video: v });
    svc.localStream = await svc.media.getUserMedia({ audio, video: v });

    // повесим «стражей» на аудио-трек
    attachAudioGuards(svc);
    // дернём UI
    try { svc.emitter.emit('local-stream-updated', svc.localStream); } catch { }
}

export function reactivateTracks(svc) {
    try {
        const before = {
            a: (svc.localStream?.getAudioTracks?.() || []).map(t => t.enabled),
            v: (svc.localStream?.getVideoTracks?.() || []).map(t => t.enabled),
        };
        svc.localStream?.getAudioTracks?.().forEach(t => { if (t.enabled === false) t.enabled = true; });
        svc.localStream?.getVideoTracks?.().forEach(t => { if (t.enabled === false) t.enabled = true; });
        const after = {
            a: (svc.localStream?.getAudioTracks?.() || []).map(t => t.enabled),
            v: (svc.localStream?.getVideoTracks?.() || []).map(t => t.enabled),
        };
        log('reactivateTracks', { before, after });
    } catch { }
}

export async function listCameras() {
    let permissionStream = null;

    try {
        permissionStream = await navigator.mediaDevices
            .getUserMedia({ video: true, audio: false })
            .catch(() => null);
    } catch { }

    try {
        const devs = await navigator.mediaDevices.enumerateDevices();
        return devs.filter(d => d.kind === 'videoinput');
    } finally {
        try {
            permissionStream?.getTracks?.().forEach((track) => track.stop());
        } catch { }
    }
}

export async function switchCameraTrack(svc, next = 'auto') {
    if (!svc.pc) throw new Error('Нет соединения');
    const currentTrack = svc.localStream?.getVideoTracks?.()[0];

    let targetFacing;
    if (next === 'auto') {
        const curFacing = currentTrack?.getSettings?.().facingMode || svc._facing || 'user';
        targetFacing = (curFacing === 'user') ? 'environment' : 'user';
    } else {
        targetFacing = next;
    }
    svc._facing = targetFacing;

    let stream;
    try {
        stream = await svc.media.getUserMedia({
            audio: false,
            video: { facingMode: { exact: targetFacing }, width: { ideal: 1280 }, height: { ideal: 720 } },
        });
    } catch {
        const cams = await listCameras();
        if (cams.length < 2) throw new Error('Нет альтернативной камеры');
        const curId = currentTrack?.getSettings?.().deviceId;
        const nextDev = cams.find(c => c.deviceId !== curId) || cams[0];
        stream = await svc.media.getUserMedia({ audio: false, video: { deviceId: { exact: nextDev.deviceId } } });
    }

    const newTrack = stream.getVideoTracks()[0];
    if (!newTrack) throw new Error('Не удалось получить видеотрек');

    const sender = svc.pc.getSenders?.().find(s => s.track && s.track.kind === 'video');
    if (sender) await sender.replaceTrack(newTrack);

    if (currentTrack) {
        svc.localStream.removeTrack(currentTrack);
        try { currentTrack.stop(); } catch { }
    }
    svc.localStream.addTrack(newTrack);
    svc.emitter.emit('local-stream-updated', svc.localStream);
}

/* -------------------- АУДИО-ГУАРДС -------------------- */

export function attachAudioGuards(svc) {
    const track = svc.localStream?.getAudioTracks?.()[0];
    if (!track) return;
    if (track.__guardsBound) return;
    track.__guardsBound = true;

    let muteTimer = null;
    const ensure = (why) => reviveAudioIfNeeded(svc, why).catch(() => { });

    track.addEventListener?.('ended', () => ensure('track-ended'));
    track.addEventListener?.('mute', () => {
        clearTimeout(muteTimer);
        muteTimer = setTimeout(() => ensure('mute-timeout'), 1200);
    });
    track.addEventListener?.('unmute', () => {
        clearTimeout(muteTimer);
        muteTimer = null;
    });

    try {
        if (!svc._mediaDeviceChangeHandler) {
            svc._mediaDeviceChangeHandler = () => {
                reviveAudioIfNeeded(svc, 'devicechange').catch(() => { });
            };
            navigator.mediaDevices?.addEventListener?.('devicechange', svc._mediaDeviceChangeHandler);
        }
    } catch { }
}

export async function reviveAudioIfNeeded(svc, reason = 'unknown') {
    try {
        if (!svc || !svc.pc || !svc.localStream) return;

        const t = svc.localStream?.getAudioTracks?.()[0];
        const bad = !t || t.readyState === 'ended' || t.muted === true;

        if (!bad) return;

        console.log('[CALLDBG][PC] [AUDIO] reviveAudioIfNeeded -> re-acquire track', {
            reason, readyState: t?.readyState, muted: t?.muted
        });

        const s = await svc.media.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            video: false
        });
        const newTrack = s.getAudioTracks()[0];
        if (!newTrack) return;

        let sender = null;
        try { sender = svc.pc?.getSenders?.().find(s => s.track && s.track.kind === 'audio'); } catch { }

        if (sender && typeof sender.replaceTrack === 'function') {
            await sender.replaceTrack(newTrack);
        } else {
            try { svc.pc?.addTrack?.(newTrack, svc.localStream); } catch { }
        }

        if (t) {
            try { svc.localStream.removeTrack(t); } catch { }
            try { t.stop(); } catch { }
        }
        svc.localStream.addTrack(newTrack);

        attachAudioGuards(svc);
        svc.emitter.emit('local-stream-updated', svc.localStream);

        console.log('[CALLDBG][PC] [AUDIO] revived OK', { reason });
    } catch (e) {
        console.warn('[AUDIO] revive failed', e?.message || e);
    }
}

/* опциональный «пинок» sender — оставляю как утилиту, но нигде не дергаю */
export async function kickAudioSender(svc) {
    try {
        const sender = svc.pc?.getSenders?.().find(s => s.track && s.track.kind === 'audio');
        const track = svc.localStream?.getAudioTracks?.()[0];
        if (!sender || !track) return false;
        await sender.replaceTrack(track);
        return true;
    } catch {
        return false;
    }
}
